/**
 * VisualEffects - Particle effects and screen shake system
 * Handles hit sparks, pickup sparkles, landing dust, robot alert bursts, and camera shake
 */

import * as THREE from 'three';

interface Particle {
  alive: boolean;
  x: number; y: number;
  vx: number; vy: number;
  life: number; maxLife: number;
  r: number; g: number; b: number;
  size: number;
}

export class VisualEffects {
  private scene: THREE.Scene;
  private particles: Particle[] = [];
  private readonly MAX_PARTICLES = 300;

  // GPU buffers
  private geometry: THREE.BufferGeometry;
  private points: THREE.Points;
  private positions: Float32Array;
  private colors: Float32Array;

  // Screen shake
  private shakeX: number = 0;
  private shakeY: number = 0;
  private shakeIntensity: number = 0;
  private shakeDuration: number = 0;
  private shakeElapsed: number = 0;

  constructor(scene: THREE.Scene) {
    this.scene = scene;

    for (let i = 0; i < this.MAX_PARTICLES; i++) {
      this.particles.push({
        alive: false, x: 0, y: 0, vx: 0, vy: 0,
        life: 0, maxLife: 1, r: 1, g: 1, b: 1, size: 4,
      });
    }

    this.positions = new Float32Array(this.MAX_PARTICLES * 3);
    this.colors    = new Float32Array(this.MAX_PARTICLES * 3);

    // Move all particles off-screen initially
    for (let i = 0; i < this.MAX_PARTICLES; i++) {
      this.positions[i * 3] = -999999;
    }

    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute('position', new THREE.BufferAttribute(this.positions, 3));
    this.geometry.setAttribute('color',    new THREE.BufferAttribute(this.colors,    3));

    const material = new THREE.PointsMaterial({
      size: 8,              // screen pixels — sizeAttenuation must be false for OrthographicCamera
      vertexColors: true,
      transparent: true,
      opacity: 1,
      depthWrite: false,
      sizeAttenuation: false, // orthographic camera: size is in pixels, not world units
    });

    this.points = new THREE.Points(this.geometry, material);
    this.points.renderOrder = 25;
    this.points.frustumCulled = false; // dead particles live at x=-999999, don't let that cull the whole batch
    this.scene.add(this.points);
  }

  // ─── Spawn helpers ─────────────────────────────────────────────────────────

  private spawn(
    x: number, y: number,
    vx: number, vy: number,
    r: number, g: number, b: number,
    life: number, _size: number,
  ): void {
    const p = this.particles.find(p => !p.alive);
    if (!p) return;
    p.alive = true;
    p.x = x; p.y = y;
    p.vx = vx; p.vy = vy;
    p.r = r; p.g = g; p.b = b;
    p.life = life; p.maxLife = life;
    p.size = _size;
  }

  // ─── Public effect triggers ─────────────────────────────────────────────────

  /** Red blood/spark burst when player takes damage */
  public spawnHitParticles(x: number, y: number): void {
    const count = 14;
    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.6;
      const speed = 80 + Math.random() * 140;
      this.spawn(
        x, y,
        Math.cos(angle) * speed,
        Math.sin(angle) * speed + 40,
        1.0, 0.08 + Math.random() * 0.15, 0.0,
        0.35 + Math.random() * 0.35,
        5 + Math.random() * 4,
      );
    }
    // Bright center flash
    this.spawn(x, y, 0, 0, 1.0, 0.7, 0.2, 0.12, 14);
  }

  /** Golden sparkle when picking up an item */
  public spawnPickupParticles(x: number, y: number): void {
    const count = 10;
    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 * i) / count;
      const speed = 50 + Math.random() * 90;
      this.spawn(
        x, y,
        Math.cos(angle) * speed,
        Math.abs(Math.sin(angle)) * speed + 40,
        1.0, 0.85 + Math.random() * 0.15, Math.random() * 0.2,
        0.5 + Math.random() * 0.4,
        4 + Math.random() * 5,
      );
    }
  }

  /** Dust puff on hard landing */
  public spawnLandingDust(x: number, y: number): void {
    const count = 8;
    for (let i = 0; i < count; i++) {
      const side = i < count / 2 ? -1 : 1;
      const vx = side * (20 + Math.random() * 70);
      const vy = 15 + Math.random() * 35;
      const gray = 0.45 + Math.random() * 0.3;
      this.spawn(x, y, vx, vy, gray, gray * 0.95, gray * 0.85, 0.2 + Math.random() * 0.2, 4 + Math.random() * 4);
    }
  }

  /** Orange/red burst when a robot spots the player */
  public spawnAlertBurst(x: number, y: number): void {
    const count = 10;
    for (let i = 0; i < count; i++) {
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * 1.4;
      const speed = 90 + Math.random() * 110;
      this.spawn(
        x, y + 32,
        Math.cos(angle) * speed,
        Math.sin(angle) * speed,
        1.0, 0.45 + Math.random() * 0.45, 0.0,
        0.4 + Math.random() * 0.35,
        6 + Math.random() * 5,
      );
    }
  }

  // ─── Screen shake ───────────────────────────────────────────────────────────

  /**
   * Trigger screen shake. If a stronger shake is already in progress, it wins.
   * @param intensity  Max pixel displacement
   * @param duration   Duration in seconds
   */
  public triggerScreenShake(intensity: number, duration: number): void {
    if (intensity > this.shakeIntensity || this.shakeElapsed >= this.shakeDuration) {
      this.shakeIntensity = intensity;
      this.shakeDuration  = duration;
      this.shakeElapsed   = 0;
    }
  }

  /** Returns current camera shake offset (call ONCE per frame, returns {x, y}) */
  public getShakeOffset(): { x: number; y: number } {
    return { x: this.shakeX, y: this.shakeY };
  }

  // ─── Per-frame update ───────────────────────────────────────────────────────

  public update(deltaTime: number): void {
    // ── Shake ──
    if (this.shakeElapsed < this.shakeDuration) {
      this.shakeElapsed += deltaTime;
      const fade = 1.0 - (this.shakeElapsed / this.shakeDuration);
      this.shakeX = (Math.random() - 0.5) * 2 * this.shakeIntensity * fade;
      this.shakeY = (Math.random() - 0.5) * 2 * this.shakeIntensity * fade;
    } else {
      this.shakeX = 0;
      this.shakeY = 0;
    }

    // ── Particles ──
    for (let i = 0; i < this.MAX_PARTICLES; i++) {
      const p = this.particles[i]!;
      if (!p.alive) {
        this.positions[i * 3] = -999999; // keep off-screen
        continue;
      }

      p.life -= deltaTime;
      if (p.life <= 0) {
        p.alive = false;
        this.positions[i * 3] = -999999;
        continue;
      }

      p.vy -= 220 * deltaTime; // gravity
      p.x  += p.vx * deltaTime;
      p.y  += p.vy * deltaTime;

      const fade = p.life / p.maxLife;
      this.positions[i * 3]     = p.x;
      this.positions[i * 3 + 1] = p.y;
      this.positions[i * 3 + 2] = 2; // z above all world geometry

      this.colors[i * 3]     = p.r * fade;
      this.colors[i * 3 + 1] = p.g * fade;
      this.colors[i * 3 + 2] = p.b * fade;
    }

    (this.geometry.attributes['position'] as THREE.BufferAttribute).needsUpdate = true;
    (this.geometry.attributes['color']    as THREE.BufferAttribute).needsUpdate = true;
  }

  public destroy(): void {
    this.scene.remove(this.points);
    this.geometry.dispose();
    (this.points.material as THREE.Material).dispose();
  }
}
